/**
 * Copy for the public site. Everything here describes what ninekeep does today; anything not
 * built yet says so ("coming"). Vendor facts are quoted only where checked against the
 * vendor's own published SLA (2026-10-19); otherwise a vendor page speaks about the contract.
 */

export interface Section {
  heading: string;
  text?: string;
  bullets?: string[];
}

export interface Entry {
  slug: string;
  title: string;
  /** One line for menus and cards. */
  summary: string;
  /** The page's opening paragraph. */
  lead: string;
  sections: Section[];
  /** Shown as a label when the thing isn't built yet. */
  coming?: boolean;
  /** The example drawn beside the opening paragraph. */
  visual?: Visual;
  /** Three short wins, shown as cards under the opening. */
  highlights?: [title: string, text: string][];
  faq?: [question: string, answer: string][];
}

export type Visual =
  | "memo"
  | "clause"
  | "deadlines"
  | "scorecard"
  | "portfolio"
  | "sorted"
  | "checks"
  | "recovered"
  | "small";

export interface Vendor {
  slug: string;
  name: string;
  category: string;
  /** The vendor's public status page: one of the places ninekeep watches (each checked to respond). */
  status: string;
  /** Checked against the vendor's published SLA; absent when terms vary by product and plan. */
  published?: {
    source: string;
    /** When we read the published terms. */
    checked: string;
    promise: string;
    credits: string[];
    deadline: string;
    howToClaim: string;
    note?: string;
  };
}

export const PRODUCTS: Entry[] = [
  {
    slug: "contract-reader",
    title: "Contract reader",
    summary: "Finds what each vendor promised you, and what a miss is worth.",
    lead: "Upload a vendor contract as a PDF, Word file or text. ninekeep lists what the vendor promised, what you get back when they miss, and the last day to ask. It quotes the contract word for word, so you can check it.",
    visual: "clause",
    sections: [
      {
        heading: "What it finds",
        bullets: [
          "How much uptime the vendor promised",
          "How fast they promised to answer support requests",
          "What you get back for each miss",
          "How and when to ask for it",
          "When the contract renews",
        ],
      },
      {
        heading: "You check it first",
        text: "Nothing counts until you approve it. Each line shows the words it came from. If the contract is unclear, ninekeep asks you a simple question.",
      },
      {
        heading: "When a contract pays nothing",
        text: "Some contracts promise uptime but give nothing back when it's missed. ninekeep tells you, and still records each miss for your next renewal talk.",
      },
    ],
  },
  {
    slug: "vendor-watch",
    title: "Vendor monitoring",
    summary: "Checks each vendor against what your contract promised.",
    lead: "ninekeep keeps an eye on every vendor and writes down every outage, with the date and time. Each month, it checks the vendor against what your contract promised.",
    visual: "checks",
    sections: [
      {
        heading: "How it knows",
        bullets: [
          "The vendor's public status page",
          "Its own checks on the vendor's service",
          "Outage emails the vendor sends you, forwarded to ninekeep",
          "Google Cloud's service health, if you use Google Cloud",
        ],
      },
      {
        heading: "Quick to set up",
        text: "Add a vendor and ninekeep suggests how to monitor it. You confirm before it starts.",
      },
      {
        heading: "A record over time",
        text: "Each vendor gets a month-by-month record, so you can see who lets you down most, and what it cost.",
      },
    ],
  },
  {
    slug: "claims",
    title: "Claims",
    summary: "Writes, sends and follows each claim until you're paid.",
    lead: "When a vendor misses its promise, ninekeep works out what you're owed, shows how, and writes the claim for you, before the deadline.",
    visual: "memo",
    sections: [
      {
        heading: "Every number shown",
        text: "Each claim shows the fee, the uptime, and how the amount was worked out. Anyone can check it, including the vendor.",
      },
      {
        heading: "You stay in charge",
        bullets: [
          "You read each claim before it goes, or let small ones send on their own",
          "Proof of each outage goes with the claim, as a PDF and a spreadsheet",
          "ninekeep reads the vendor's reply and tells you what it means",
          "If the vendor pushes back, ninekeep writes your answer from the contract",
        ],
      },
      {
        heading: "Until you're paid",
        text: "ninekeep follows each claim until the money shows up on your bill, and reminds you if the vendor goes quiet.",
      },
    ],
  },
  {
    slug: "deadline-alerts",
    title: "Deadline alerts",
    summary: "Tells you before a deadline passes.",
    lead: "Some contracts give you only 15 days after the month ends to ask. Some want you to tell them during the outage. ninekeep knows each deadline and puts the next one at the top of your list.",
    visual: "deadlines",
    sections: [
      {
        heading: "What it reminds you about",
        bullets: [
          "The last day to claim each month",
          "Outages you need to report right away",
          "Contract renewals, so you can negotiate in time",
          "Claims the vendor hasn't answered after 30 days",
        ],
      },
    ],
  },
  {
    slug: "scorecards-and-renewals",
    title: "Scorecards and renewals",
    summary: "Each vendor's track record, ready for your renewal talk.",
    lead: "ninekeep keeps a record of every month a vendor missed, with proof, even when the contract paid nothing. When the contract comes up for renewal, that record helps you get a better deal.",
    visual: "scorecard",
    sections: [
      {
        heading: "What you bring to the talk",
        bullets: [
          "Every month the vendor missed, with proof",
          "What you claimed, what was paid, and what's still open",
          "Which vendors give nothing back at all",
        ],
      },
      {
        heading: "Across many companies",
        text: "A single view across several companies is coming. Today, each company sees its own.",
      },
    ],
  },
];

export const SOLUTIONS: Entry[] = [
  {
    slug: "sla-credit-recovery",
    title: "Get back what you're owed",
    summary: "Claim the money your contracts already promise.",
    lead: "Many vendor contracts promise money back when the vendor lets you down. To get it, someone has to notice, measure, work out the amount and ask. ninekeep does all four, for every vendor, every month.",
    visual: "memo",
    highlights: [
      [
        "Noticed",
        "Every month for every vendor, measured against your contract, not the vendor's own status page.",
      ],
      [
        "Worked out",
        "The credit from the contract's own formula, with every step of the math shown.",
      ],
      [
        "Claimed",
        "Written with proof, sent when you say so, and followed until it's on your invoice.",
      ],
    ],
    faq: [
      [
        "How much does a credit come to?",
        "Usually a share of that month's fee for the service that went down: often 10% or more, depending on how far the vendor fell short. Your contract sets the exact amount.",
      ],
      [
        "Do vendors actually pay?",
        "When the claim is on time, in the right place and backed by proof, yes: it's in their own contract. ninekeep makes sure all three are true.",
      ],
      [
        "What does it cost?",
        "A free audit first. Then $4 per vendor a month plus 15% of what comes back, only once it's on your bill.",
      ],
    ],
    sections: [
      {
        heading: "How you get paid",
        text: "It's usually a share of that month's fee, taken off a later bill. One claim is small. Across many vendors and months, it adds up.",
      },
      {
        heading: "What you need",
        text: "Just your vendor contracts. ninekeep does the rest, and you approve what goes out.",
      },
    ],
  },
  {
    slug: "never-miss-a-deadline",
    title: "Never miss a deadline",
    summary: "Short deadlines, tracked for you.",
    lead: "A lot of money is lost because nobody asked in time. Atlassian gives you 15 days after the month ends. AWS gives you about two months. ninekeep knows each deadline and claims before it.",
    visual: "deadlines",
    highlights: [
      ["Every clock known", "ninekeep reads each contract's deadline and when it starts counting."],
      ["Claimed before it closes", "Claims are ready well before the window ends, not after."],
      [
        "Told in time",
        "When a contract needs you to report an outage while it's happening, ninekeep tells you right away.",
      ],
    ],
    faq: [
      [
        "Why are deadlines missed so often?",
        "They're short, they start at different points in different contracts, and nobody owns them. By the time the invoice arrives, the window has often closed.",
      ],
      [
        "Which deadlines does ninekeep track?",
        "The claim window in each contract you upload, any notice the vendor needs during an outage, and your renewal dates.",
      ],
    ],
    sections: [
      {
        heading: "When you must speak up during the outage",
        text: "Some contracts only pay if you report the outage right away. ninekeep tells you as soon as it sees one.",
      },
    ],
  },
  {
    slug: "vendor-renewals",
    title: "Better renewal deals",
    summary: "Go into your renewal talk with the vendor's track record.",
    lead: 'Renewals are where the real savings are. A dated record of every month the vendor missed turns "we\'ve had some issues" into hard facts.',
    visual: "scorecard",
    highlights: [
      ["A dated record", "Every month each vendor kept or missed its promise, with the proof."],
      ["Money on the table", "What you claimed, what was paid, and what's still open."],
      ["Reminded in time", "A heads-up before each renewal deadline, with the record ready."],
    ],
    faq: [
      [
        "How does this help a renewal?",
        "A record of missed months, with proof, turns a vague complaint into numbers. It's leverage for a better price, stronger terms, or a reason to switch.",
      ],
      ["Does the vendor see the scorecard?", "Only if you share it. It's yours."],
    ],
    sections: [
      {
        heading: "In good time",
        text: "ninekeep reminds you before each renewal deadline, with that vendor's record ready.",
      },
    ],
  },
  {
    slug: "portfolio-value-creation",
    title: "Savings across a portfolio",
    summary: "An easy win for every company you own.",
    lead: "For private equity firms: every company you own pays software vendors that miss their promises. Introduce ninekeep once, and each company will get a free audit, and ninekeep then monitors its vendors every month.",
    visual: "portfolio",
    highlights: [
      ["One introduction", "You introduce ninekeep once to each company's finance lead."],
      ["A free audit each", "Every company gets its own audit and its own private account."],
      ["Money back, company by company", "Each company sees what it was owed and what came back."],
    ],
    faq: [
      ["Do our companies share data?", "No. Each company's data stays private to that company."],
      [
        "Is there a view across all of them?",
        "A single view across your companies is coming. Today each company has its own account and results.",
      ],
      [
        "What does it cost the companies?",
        "The same as anyone: a free audit, then $4 per vendor a month plus 15% of what comes back.",
      ],
    ],
    sections: [
      {
        heading: "How it works",
        bullets: [
          "You introduce ninekeep to each company's finance lead",
          "Each company will get its own free audit and its own account",
          "Each company sees what it was owed, and what came back",
        ],
      },
      {
        heading: "One view of everything",
        text: "A single view across all your companies is coming.",
      },
    ],
  },
  {
    slug: "know-what-you-are-owed",
    title: "Know who owes you nothing",
    summary: "See which contracts pay you back, and which don't.",
    lead: "Many contracts promise uptime but give nothing back when it's missed. ninekeep sorts your vendors into three groups: pays you back, pays nothing, and no contract yet. Then you know where to push at renewal.",
    visual: "sorted",
    highlights: [
      ["Pays you back", "Contracts with a credit, and what each miss is worth."],
      ["Pays nothing", "Promises with no money behind them, the ones to fix at renewal."],
      ["No contract yet", "Vendors you pay without terms on file, so you know where to look."],
    ],
    faq: [
      [
        "Why would a contract pay nothing?",
        "Many promise uptime but name no credit, or exclude so much that a miss never counts. You only find out by reading it.",
      ],
      [
        "What do I do about the ones that pay nothing?",
        "Ask for a credit clause at renewal. ninekeep reminds you before each renewal, with the vendor's record.",
      ],
    ],
    sections: [],
  },
];

export const INDUSTRIES: Entry[] = [
  {
    slug: "software-and-technology",
    title: "Software and technology",
    summary: "Companies that run on cloud and developer tools.",
    lead: "Software companies rely on cloud hosting, developer tools and messaging services. When those vendors go down, so do you. Your contract may owe you money for it.",
    visual: "checks",
    sections: [
      {
        heading: "Vendors we often see",
        bullets: [
          "Cloud hosting and databases",
          "Code hosting",
          "Monitoring tools",
          "Email and text messaging services",
        ],
      },
    ],
  },
  {
    slug: "financial-services",
    title: "Financial services",
    summary: "Firms that already keep a close eye on vendors.",
    lead: "Financial firms already track their vendors closely. ninekeep turns that tracking into money back, plus a dated record of how each vendor did.",
    visual: "recovered",
    sections: [
      {
        heading: "Fits how you already work",
        text: "Every number comes with the contract's words and proof, so each claim holds up to review.",
      },
    ],
  },
  {
    slug: "healthcare",
    title: "Healthcare",
    summary: "Care providers and health tech companies that run on software.",
    lead: "Healthcare teams rely on software for scheduling, billing and messaging. When those vendors miss their promises, ninekeep claims what your contracts owe you.",
    visual: "clause",
    sections: [
      {
        heading: "What ninekeep needs",
        text: "Only your vendor contracts and the vendors' public status pages. ninekeep never needs patient data or access to your systems.",
      },
    ],
  },
  {
    slug: "retail-and-ecommerce",
    title: "Retail and ecommerce",
    summary: "Stores, payments and support tools that can't go down.",
    lead: "When your store, payments or support tool goes down, you lose sales that day. Your contract often owes you money for it. ninekeep makes sure you ask.",
    visual: "memo",
    sections: [],
  },
  {
    slug: "manufacturing-and-distribution",
    title: "Manufacturing and distribution",
    summary: "Business software that runs in the cloud.",
    lead: "More manufacturers now run their planning, sales and inventory software in the cloud. Those contracts have uptime promises too, and the people who own them rarely monitor them for outages.",
    visual: "sorted",
    sections: [],
  },
  {
    slug: "professional-services",
    title: "Professional services",
    summary: "Firms whose work runs on shared software.",
    lead: "Agencies, consultancies and law firms run on shared documents and project tools. ninekeep reads those contracts and claims what each missed month is worth.",
    visual: "deadlines",
    sections: [],
  },
];

export const CUSTOMERS: Entry[] = [
  {
    slug: "finance-leaders",
    title: "Finance",
    summary: "A recovery audit for your software contracts.",
    lead: "This is money owed to your company that never shows up in your forecast. ninekeep gets it back, shows every number, and tells you what came back and what's still open.",
    visual: "recovered",
    highlights: [
      ["Money you didn't forecast", "Credits owed to your company that nobody was claiming."],
      ["Every number shown", "Each claim comes with its math and proof, ready for review."],
      ["Tracked until paid", "Each credit recorded when it lands, and chased when it doesn't."],
    ],
    faq: [
      [
        "How does it show up in the books?",
        "As a credit on the vendor's next invoice: a lower bill. ninekeep records each one so you can match it.",
      ],
      [
        "Who approves what goes out?",
        "You do. Nothing goes to a vendor unless you say so, or after a wait you choose for small claims.",
      ],
    ],
    sections: [
      {
        heading: "What you get",
        bullets: [
          "A running total of money recovered",
          "Every claim with its math and proof",
          "Each payment recorded when it lands, and chased when it doesn't",
        ],
      },
    ],
  },
  {
    slug: "procurement",
    title: "Procurement",
    summary: "Hold vendors to what they signed.",
    lead: "You negotiated the promise. ninekeep holds the vendor to it every month, and gives you their track record before it's time to renew.",
    visual: "scorecard",
    highlights: [
      ["Promises in their own words", "Every contract's terms, quoted from the contract."],
      ["Renewals with leverage", "Each vendor's track record before it's time to renew."],
      ["Who pays you back", "Vendors sorted by whether a miss is worth anything."],
    ],
    faq: [
      [
        "Does it replace our contract system?",
        "No. It reads the vendor contracts you upload and holds vendors to them. Keep your contracts wherever they live.",
      ],
      [
        "Can we use it for negotiations?",
        "Yes: the scorecard shows each month a vendor missed, with proof, ready for the renewal talk.",
      ],
    ],
    sections: [
      {
        heading: "What you get",
        bullets: [
          "Every contract's promises, in the contract's own words",
          "Reminders before each renewal",
          "Your vendors sorted by who pays you back",
        ],
      },
    ],
  },
  {
    slug: "it-and-operations",
    title: "IT and operations",
    summary: "Turn the outages you live through into money back.",
    lead: "You feel every vendor outage first. ninekeep writes each one down against the contract, so the outage your team fought through turns into money back.",
    visual: "checks",
    highlights: [
      [
        "Watched three ways",
        "The vendor's status page, ninekeep's own checks, and the outage emails you forward.",
      ],
      ["Outages become proof", "Each one written down against the contract, with times."],
      ["Nothing to install", "ninekeep never needs access to your systems."],
    ],
    faq: [
      [
        "What do we have to set up?",
        "Add a vendor and ninekeep suggests how to monitor it. Forwarding the vendor's outage emails is optional, and they count as proof.",
      ],
      [
        "Does ninekeep need our logs or monitoring?",
        "No. It works from your contracts and public information.",
      ],
    ],
    sections: [
      {
        heading: "Little to set up",
        text: "Add a vendor and ninekeep suggests how to monitor it. Forward the vendor's outage emails to ninekeep, and they count as proof.",
      },
    ],
  },
  {
    slug: "pe-operating-partners",
    title: "Private equity firms",
    summary: "Introduce ninekeep once, and every company is covered.",
    lead: "Introduce ninekeep once, and each company you own will get a free audit and its own account. The money their vendors owe them gets claimed.",
    visual: "portfolio",
    highlights: [
      ["Introduce once", "ninekeep sets up each company with its own free audit and account."],
      ["Real savings, fast", "Each audit covers up to 5 contracts and the last 12 months."],
      ["Private by company", "Each company's data stays its own."],
    ],
    faq: [
      [
        "How long does an audit take?",
        "Upload the contracts and ninekeep reads them in minutes. Approving what it found takes a few more.",
      ],
      [
        "Is there a portfolio-wide view?",
        "It's coming. Today each company has its own account and results.",
      ],
    ],
    sections: [
      {
        heading: "How it works",
        bullets: [
          "A free audit at each company: up to 5 contracts, the last 12 months",
          "Each company's data stays private to that company",
          "A single view across all your companies is coming",
        ],
      },
    ],
  },
  {
    slug: "small-companies",
    title: "Small companies",
    summary: "Up to 5 vendors for $20 a month, plus 15% of what comes back.",
    lead: "The same price as everyone, at the smallest size: $20 a month covers up to 5 vendors. A free audit shows what you were owed before you pay anything.",
    visual: "small",
    highlights: [
      ["The same product", "Everything bigger companies get, at the smallest size."],
      ["Priced to start", "Up to 5 vendors for $20 a month, plus 15% of what comes back."],
      ["Free audit first", "See what you were owed before you pay anything."],
    ],
    faq: [
      [
        "Is it worth it for a small company?",
        "The free audit tells you. If your vendors owed you less than the plan costs, you'll know before you pay a cent.",
      ],
      ["Do I need a card for the audit?", "No."],
    ],
    sections: [],
  },
];

export const VENDORS: Vendor[] = [
  {
    slug: "aws",
    name: "Amazon Web Services (EC2)",
    category: "Cloud infrastructure",
    status: "https://health.aws.amazon.com/health/status",
    published: {
      checked: "2026-10-19",
      source: "https://aws.amazon.com/compute/sla/",
      promise:
        "99.99% monthly uptime per region when running across two or more Availability Zones; 99.5% for a single instance",
      credits: ["Below 99.99% (region): 10%", "Below 99.0%: 30%", "Below 95.0%: 100%"],
      deadline: "By the end of the second billing cycle after the incident",
      howToClaim:
        "A case in the AWS Support Center, with the claim wording AWS specifies in the subject line",
      note: "The money comes off future EC2 bills, and only if it is more than $1 that month.",
    },
  },
  {
    slug: "atlassian",
    name: "Atlassian Cloud (Jira, Confluence)",
    category: "Collaboration",
    status: "https://status.atlassian.com",
    published: {
      checked: "2026-10-19",
      source: "https://www.atlassian.com/legal/sla",
      promise: "99.9% monthly uptime on Premium, 99.95% on Enterprise",
      credits: [
        "Premium: 10% below 99.9%, 25% below 99.0%, 50% below 95.0%",
        "Enterprise: 5% below 99.95%, then 10%, 25% and 50%",
      ],
      deadline: "Within 15 days after the end of the month",
      howToClaim: "A ticket at support.atlassian.com, with every field completed",
      note: "Atlassian's own records decide whether it missed, so your claim needs to match them.",
    },
  },
  {
    slug: "slack",
    name: "Slack",
    category: "Collaboration",
    status: "https://slack-status.com",
    published: {
      checked: "2026-10-19",
      source: "https://slack.com/terms/service-level-agreement",
      promise:
        "Commercially reasonable efforts to keep the service available, except planned downtime and events outside its control",
      credits: [
        "No credit table on the public page: credits, where they exist, are in your order form",
      ],
      deadline: "As your order form sets out",
      howToClaim: "As your order form sets out",
    },
  },
  {
    slug: "google-cloud",
    name: "Google Cloud (Compute Engine)",
    category: "Cloud infrastructure",
    status: "https://status.cloud.google.com",
    published: {
      source: "https://cloud.google.com/compute/sla",
      checked: "2026-10-24",
      promise:
        "99.99% monthly uptime for instances across multiple zones (Premium tier); 99.9% for most single instances",
      credits: ["Below 99.99%: 10%", "Below 99.0%: 25%", "Below 95.0%: 100%"],
      deadline: "Within 60 days of becoming eligible for the credit",
      howToClaim:
        "Notify Google technical support, with log files showing when the downtime happened",
      note: "The money comes off future bills, up to that month's bill. Without your logs, you get nothing.",
    },
  },
  {
    slug: "microsoft-azure",
    name: "Microsoft Azure",
    category: "Cloud infrastructure",
    status: "https://azure.status.microsoft",
  },
  {
    slug: "digitalocean",
    name: "DigitalOcean (Droplets)",
    category: "Cloud infrastructure",
    status: "https://status.digitalocean.com",
    published: {
      source: "https://www.digitalocean.com/sla/cpu-droplets",
      checked: "2026-10-24",
      promise: "99.99% monthly uptime for each Droplet, measured one Droplet at a time",
      credits: ["Below 99.99%: 100% of that Droplet's charges"],
      deadline: "Within two billing cycles of the month the downtime happened",
      howToClaim:
        "Email success@digitalocean.com with your account email, the affected Droplets and the outage times",
      note: "The money only comes off future Droplet bills.",
    },
  },
  {
    slug: "salesforce",
    name: "Salesforce",
    category: "CRM and sales",
    status: "https://status.salesforce.com",
  },
  {
    slug: "hubspot",
    name: "HubSpot",
    category: "CRM and sales",
    status: "https://status.hubspot.com",
  },
  {
    slug: "zendesk",
    name: "Zendesk",
    category: "Customer support",
    status: "https://status.zendesk.com",
  },
  {
    slug: "twilio",
    name: "Twilio (APIs)",
    category: "Communications",
    status: "https://status.twilio.com",
    published: {
      source: "https://www.twilio.com/en-us/legal/service-level-agreement/twilio-apis",
      checked: "2026-10-24",
      promise: "99.95% monthly API availability; 99.99% with Twilio Enterprise Edition",
      credits: ["Below the threshold: 10% of that month's fees for the affected APIs"],
      deadline: "Within 30 days from the last day of the month",
      howToClaim:
        "A support ticket at help.twilio.com titled “SLA Claim”, with the outage times and your documentation",
      note: "Outages under five minutes don't count. The money only comes off future Twilio bills.",
    },
  },
  {
    slug: "github",
    name: "GitHub",
    category: "Developer tools",
    status: "https://www.githubstatus.com",
  },
  {
    slug: "linear",
    name: "Linear",
    category: "Developer tools",
    status: "https://linearstatus.com",
  },
  { slug: "box", name: "Box", category: "Files and content", status: "https://status.box.com" },
  {
    slug: "okta",
    name: "Okta (Auth0 platform)",
    category: "Identity",
    status: "https://status.okta.com",
    published: {
      source:
        "https://okta.com/sites/default/files/2022-02/ServiceLevelAgreement-for-Auth0-Platform-02-7-22.pdf",
      checked: "2026-10-24",
      promise: "99.99% average monthly availability for the Auth0 platform's core services",
      credits: [
        "Below 99.99%: 5%",
        "Below 99.9%: 10%",
        "Below 99.0%: 20%",
        "Below 95%: 50% of the monthly fee",
      ],
      deadline: "In writing, within five business days of the event",
      howToClaim: "Contact Okta in writing",
      note: "From Okta's published Auth0 terms (February 2022). Five business days is the shortest deadline we've seen. Miss it and the money is gone.",
    },
  },
  { slug: "zoom", name: "Zoom", category: "Communications", status: "https://status.zoom.us" },
  {
    slug: "docusign",
    name: "Docusign",
    category: "Files and content",
    status: "https://status.docusign.com",
  },
];

/** The one price after the free audit; the server's billing uses the same numbers. */
export const PRICE = { perVendor: 4, minVendors: 5, successFee: 15, auditContracts: 5 } as const;

export const FAIR_USE = [
  "The free audit costs nothing and needs no card",
  "The 15% only comes out of money ninekeep got back, once it's on your bill",
  "Each vendor counts once, however many contracts you upload for it",
  "Plans start at $20 a month, for up to 5 vendors",
  "Stop any time: the claims ninekeep wrote are yours",
];
